// Scoped-data hook for W2A-lower screens (12-22).
//
// Replaces fixture-adapter defaults with real reads against the signed
// api-server. Preview mode (?preview=sarah OR dev-mock session OR a
// <PreviewFixtureProvider />) still returns the fixture synchronously so
// R8 companion-state tests and the design review harness keep rendering
// the walkthrough deck without a round trip.
//
// This module intentionally does NOT live under screens/_shared/ (W1B's
// lane). It is a sibling helper owned by W2A-lower and only consumed by
// screens 12-22.
//
// W31 (2026-10-20): the fetch now sends the same session headers as
// `buildSessionHeaders` (Authorization, X-Customer-Id, X-Portal-Id,
// X-Work-Id when a work order is attached, and X-User-Id / X-User-Email
// on url-bootstrap sessions) and the result exposes `previewAllowed` so a
// screen can hide the test-harness state switcher and the "Design
// preview" pill on a real session.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { BoundaryState } from '../_shared/DataBoundary.js';
import { isDevMockSession } from '../../auth/mockContext.js';
import { useOptionalSession } from '../../auth/session-context.js';
import { usePreviewFixtureMode } from '../_shared/useAuthenticatedCustomer.js';
import { buildSessionHeaders } from '../_shared/useContactProfile.js';

// Shape returned to the caller. `data` is defined when `state.kind` is
// 'default'; otherwise the DataBoundary renders the companion state.
export interface ScopedData<T> {
  state: BoundaryState;
  data: T | null;
  /**
   * True when the design fixture is allowed on screen: the dev mock
   * session or an explicit ?preview= flag. False on every real session,
   * including while the session handshake is still pending.
   */
  previewAllowed: boolean;
}

export interface UseScopedDataArgs<T> {
  // Logical endpoint path, e.g. `/v1/contacts/${customerId}/addresses`.
  // Pass null when the screen is intentionally stand-alone (preview only).
  path: string | null;
  // Fixture payload. Rendered in preview mode or when forced by a test.
  fixture: T;
  // Human label for the loading state.
  loadingLabel?: string;
  // Error title + code for the error state.
  errorTitle?: string;
  errorCode?: string;
  // Explicit opt-out for empty detection. Default: null/undefined/[] empty.
  isEmpty?: (payload: unknown) => boolean;
  // Honest copy for the empty state (defaults kept for older callers).
  emptyTitle?: string;
  emptyBody?: string;
  // Hard-override from the test harness: when provided we skip the fetch.
  stateOverride?: BoundaryState;
  // Optional fetch injection for tests. Defaults to window.fetch.
  fetchImpl?: typeof fetch;
}

// Narrow helper used by the default empty detector.
function defaultIsEmpty(payload: unknown): boolean {
  if (payload === null || payload === undefined) return true;
  if (Array.isArray(payload) && payload.length === 0) return true;
  if (typeof payload === 'object' && Object.keys(payload as object).length === 0) {
    return true;
  }
  return false;
}

function readRuntimeBaseUrl(): string {
  const injected =
    typeof window !== 'undefined'
      ? (window as { __RUNTIME_API_BASE_URL__?: string }).__RUNTIME_API_BASE_URL__
      : undefined;
  if (typeof injected === 'string' && injected.length > 0) return injected;
  const env = (import.meta as unknown as {
    env?: Record<string, string | undefined>;
  }).env;
  const fromEnv = env?.VITE_RUNTIME_API_BASE_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return '';
}

/**
 * Resolve the authoritative state for a W2A-lower screen.
 *
 *  - `stateOverride` wins (R8 companion-state tests).
 *  - Preview mode (dev-mock session or `?preview=sarah` or
 *    <PreviewFixtureProvider />) returns the fixture synchronously.
 *  - Production: issue a signed GET to `path`. Returns loading / error /
 *    unauthorized / empty states that DataBoundary already renders.
 *
 * The hook does not build a WorkClient instance per render. It issues a
 * single fetch per path with the session JWT in Authorization + the
 * X-Customer-Id / X-Portal-Id context headers established in
 * ../../api/transport.ts.
 */
export function useScopedData<T>(args: UseScopedDataArgs<T>): ScopedData<T> {
  const { path, fixture, stateOverride, isEmpty, fetchImpl } = args;
  const loadingLabel = args.loadingLabel ?? 'Loading…';
  const errorTitle = args.errorTitle ?? 'Could not load.';
  const errorCode = args.errorCode ?? 'SCOPED_DATA_UNAVAILABLE';
  const emptyTitle = args.emptyTitle ?? 'Nothing to review yet.';
  const emptyBody = args.emptyBody;

  const session = useOptionalSession();
  const previewFlag = usePreviewFixtureMode();
  const devMock = isDevMockSession(session?.session ?? null);
  const previewAllowed = previewFlag || devMock;

  const [prodState, setProdState] = useState<Omit<ScopedData<T>, 'previewAllowed'>>(() => ({
    state: { kind: 'loading', label: loadingLabel },
    data: null,
  }));

  // Keep the fetch deps pinned to mount so a token refresh does not cancel
  // the in-flight request.
  const depsRef = useRef<{ fetchImpl: typeof fetch | null; baseUrl: string }>({
    fetchImpl:
      fetchImpl ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null),
    baseUrl: readRuntimeBaseUrl(),
  });

  // W31: pinned in a ref so an inline `isEmpty` arrow from a caller does
  // not re-run the fetch effect on every render.
  const emptyRef = useRef<(payload: unknown) => boolean>(isEmpty ?? defaultIsEmpty);
  emptyRef.current = isEmpty ?? defaultIsEmpty;

  // Only runs in production path. Preview short-circuits before this.
  useEffect(() => {
    if (previewAllowed) return;
    if (stateOverride) return;
    if (!session || session.status === 'pending') {
      setProdState({ state: { kind: 'loading', label: loadingLabel }, data: null });
      return;
    }
    if (
      session.status === 'error' ||
      session.status === 'expired' ||
      !session.session
    ) {
      setProdState({
        state: {
          kind: 'blocked',
          title: 'Session unavailable',
          reason:
            'Open the Sales Workspace from a HubSpot contact record and retry.',
          gateName: 'SESSION_UNAVAILABLE',
        },
        data: null,
      });
      return;
    }
    if (!path) {
      // No endpoint declared for this screen. The chrome stays blocked
      // until the production path is finalised.
      setProdState({
        state: {
          kind: 'blocked',
          title: 'Live data not wired',
          reason:
            'The authenticated production route for this screen has not been published yet.',
          gateName: 'LIVE_WRITE_GATE_OFF',
        },
        data: null,
      });
      return;
    }
    const impl = depsRef.current.fetchImpl;
    if (!impl) {
      setProdState({
        state: {
          kind: 'error',
          title: errorTitle,
          detail: 'No fetch implementation is available in this environment.',
          errorCode,
        },
        data: null,
      });
      return;
    }
    const base = depsRef.current.baseUrl.endsWith('/')
      ? depsRef.current.baseUrl.slice(0, -1)
      : depsRef.current.baseUrl;
    const url = `${base}${path}`;
    let cancelled = false;
    setProdState({
      state: { kind: 'loading', label: loadingLabel },
      data: null,
    });
    void (async () => {
      try {
        const response = await impl(url, {
          method: 'GET',
          headers: buildSessionHeaders(session.session!),
        });
        if (cancelled) return;
        if (response.status === 401 || response.status === 403) {
          setProdState({
            state: {
              kind: 'blocked',
              title: 'Not authorized',
              reason:
                'This record is outside your assigned scope. Ask a manager to re-route.',
              gateName: 'REP_SCOPE_DENIED',
            },
            data: null,
          });
          return;
        }
        if (response.status === 404) {
          setProdState({
            state: {
              kind: 'empty',
              title: 'Nothing to review yet.',
              body: 'No records are available for this customer.',
            },
            data: null,
          });
          return;
        }
        if (!response.ok) {
          setProdState({
            state: {
              kind: 'error',
              title: errorTitle,
              detail: `Request failed with status ${response.status}.`,
              errorCode,
            },
            data: null,
          });
          return;
        }
        let body: unknown;
        try {
          body = await response.json();
        } catch (parseErr) {
          if (cancelled) return;
          setProdState({
            state: {
              kind: 'error',
              title: errorTitle,
              detail:
                parseErr instanceof Error
                  ? parseErr.message
                  : 'Response was not valid JSON.',
              errorCode,
            },
            data: null,
          });
          return;
        }
        if (cancelled) return;
        if (emptyRef.current(body)) {
          setProdState({
            state: {
              kind: 'empty',
              title: emptyTitle,
              ...(emptyBody ? { body: emptyBody } : {}),
            },
            data: null,
          });
          return;
        }
        setProdState({ state: { kind: 'default' }, data: body as T });
      } catch (err) {
        if (cancelled) return;
        setProdState({
          state: {
            kind: 'error',
            title: errorTitle,
            detail:
              err instanceof Error
                ? err.message
                : 'Network error while loading data.',
            errorCode,
          },
          data: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    previewAllowed,
    stateOverride,
    session,
    path,
    loadingLabel,
    errorTitle,
    errorCode,
    emptyTitle,
    emptyBody,
  ]);

  return useMemo<ScopedData<T>>(() => {
    if (stateOverride) {
      // The test harness drives the state explicitly. Fixtures still back the
      // 'default' render so stateOverride={kind:'default'} shows the walkthrough.
      return { state: stateOverride, data: fixture, previewAllowed };
    }
    if (previewAllowed) {
      return { state: { kind: 'default' }, data: fixture, previewAllowed };
    }
    return { ...prodState, previewAllowed };
  }, [stateOverride, previewAllowed, fixture, prodState]);
}
